import {
  componentPermission,
  modulePermission,
} from './subscriptionPermissionRegistry';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { SECURITY_EDR_SEGMENT, SECURITY_NAV_ROUTE_ITEMS, SECURITY_SOC_SEGMENT } from './securityNav';

/** Base path segment for all Security child routes (no leading/trailing slash). */
export const SECURITY_ROUTE_PATH_PREFIX = '/mindtime/security/';

/**
 * Slugs for Security pages with dedicated implementations in routes.tsx.
 * Add new entries here when a page is built; keep placeholder nav in sync via NAV_IDS_WITH_PAGES.
 */
export const SECURITY_PAGE_ROUTES = {
  securityOperationsCenter: SECURITY_SOC_SEGMENT,
  resellerDashboard: `${SECURITY_EDR_SEGMENT}/reseller-dashboard`,
  subOrgDashboard: `${SECURITY_EDR_SEGMENT}/sub-org-dashboard`,
  clients: `${SECURITY_EDR_SEGMENT}/clients`,
  clientDetails: `${SECURITY_EDR_SEGMENT}/clients/:clientId`,
  configurationCompliance: `${SECURITY_EDR_SEGMENT}/configuration-compliance`,
  complianceDetail: `${SECURITY_EDR_SEGMENT}/configuration-compliance/:recordId`,
  deployAgent: `${SECURITY_EDR_SEGMENT}/endpoints/deploy-agent`,
  fileIntegrityMonitoring: `${SECURITY_EDR_SEGMENT}/file-integrity-monitoring`,
  fileIntegrityDetail: `${SECURITY_EDR_SEGMENT}/file-integrity-monitoring/:recordId`,
  threatHunting: `${SECURITY_EDR_SEGMENT}/threat-hunting`,
  threatHuntingDetail: `${SECURITY_EDR_SEGMENT}/threat-hunting/:recordId`,
  malwareDetection: `${SECURITY_EDR_SEGMENT}/malware-detection`,
  malwareDetail: `${SECURITY_EDR_SEGMENT}/malware-detection/:recordId`,
  automatedResponse: `${SECURITY_EDR_SEGMENT}/automated-response`,
  automatedResponseDetail: `${SECURITY_EDR_SEGMENT}/automated-response/:recordId`,
  vulnerabilityDetection: `${SECURITY_EDR_SEGMENT}/vulnerability-detection`,
  vulnerabilityDetail: `${SECURITY_EDR_SEGMENT}/vulnerability-detection/:recordId`,
  mitreAttck: `${SECURITY_EDR_SEGMENT}/mitre-attck`,
  mitreDetail: `${SECURITY_EDR_SEGMENT}/mitre-attck/:recordId`,
  endpoints: `${SECURITY_EDR_SEGMENT}/endpoints`,
  /** Optional `:tab` — see `endpointDetailsTabs.ts` (`dashboard`, `vulnerabilities`, …). */
  endpointDetails: `${SECURITY_EDR_SEGMENT}/endpoints/:systemWazuhAgentId/:tab?`,
} as const;

export const SECURITY_SUB_ROUTE_PERMISSIONS = {
  deployAgent: componentPermission(EDR_SOLUTION_ID, 'endpoints', 'deployAgent'),
  endpointDetails: modulePermission(EDR_SOLUTION_ID, 'endpoints'),
  clientDetails: modulePermission(EDR_SOLUTION_ID, 'clients'),
  complianceDetail: modulePermission(EDR_SOLUTION_ID, 'configuration-compliance'),
  fileIntegrityDetail: modulePermission(EDR_SOLUTION_ID, 'file-integrity-monitoring'),
  malwareDetail: modulePermission(EDR_SOLUTION_ID, 'malware-detection'),
  vulnerabilityDetail: modulePermission(EDR_SOLUTION_ID, 'vulnerability-detection'),
  mitreDetail: modulePermission(EDR_SOLUTION_ID, 'mitre-attck'),
  threatHuntingDetail: modulePermission(EDR_SOLUTION_ID, 'threat-hunting'),
  automatedResponseDetail: modulePermission(EDR_SOLUTION_ID, 'automated-response'),
} as const;

/** Nav item ids that map to a real page (excluded from auto-generated placeholder routes). */
const NAV_IDS_WITH_PAGES: readonly string[] = [
  'security-operations-center',
  'reseller-dashboard',
  'sub-org-dashboard',
  'clients',
  'endpoints',
  'configuration-compliance',
  'file-integrity-monitoring',
  'threat-hunting',
  'malware-detection',
  'automated-response',
  'vulnerability-detection',
  'mitre-attck',
];

/** Sidebar nav items that still use SectionPlaceholder until implemented. */
export const getSecurityPlaceholderNavItems = () =>
  SECURITY_NAV_ROUTE_ITEMS.filter((item) => !NAV_IDS_WITH_PAGES.includes(item.id));

export const toSecurityChildPath = (fullPath: string): string =>
  fullPath.replace(SECURITY_ROUTE_PATH_PREFIX, '');
